import { useState, useEffect, useCallback } from "react";
import { DollarSign, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import api from "../services/api";
import { errorToUserMessage } from "../services/errorMessages";

/**
 * Pre-launch cost estimator card. Embeds inline on a campaign row when the
 * campaign is in draft/ready status. Manager can tweak avg_segments and
 * mms_percentage; card fetches a fresh estimate from the backend on each
 * change (debounced) and shows whether the projection exceeds budget_cap.
 */
export default function CostEstimateCard({ campaignId, collapsedByDefault = true }) {
  const [expanded, setExpanded] = useState(!collapsedByDefault);
  const [avgSegments, setAvgSegments] = useState(1);
  const [mmsPercentage, setMmsPercentage] = useState(0);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEstimate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/costs/campaigns/${campaignId}/estimate`, {
        params: { avg_segments: avgSegments, mms_percentage: mmsPercentage },
      });
      setEstimate(res.data);
    } catch (err) {
      setError(errorToUserMessage(err, "Failed to load estimate"));
    } finally {
      setLoading(false);
    }
  }, [campaignId, avgSegments, mmsPercentage]);

  useEffect(() => {
    if (!expanded) return;
    const id = setTimeout(fetchEstimate, 150); // debounce slider input
    return () => clearTimeout(id);
  }, [expanded, fetchEstimate]);

  const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

  return (
    <div className="mt-3 border border-gray-200 rounded-lg bg-gray-50">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
      >
        <span className="flex items-center gap-1.5">
          <DollarSign className="w-3.5 h-3.5" />
          Pre-launch cost estimate
        </span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          {/* Assumptions */}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-gray-600">
              <span className="block mb-1">
                Avg segments per message
                <span className="text-gray-400 ml-1">({avgSegments.toFixed(1)})</span>
              </span>
              <input
                type="range"
                min={1}
                max={6}
                step={0.5}
                value={avgSegments}
                onChange={(e) => setAvgSegments(parseFloat(e.target.value))}
                className="w-full"
              />
            </label>
            <label className="text-xs text-gray-600">
              <span className="block mb-1">
                MMS percentage
                <span className="text-gray-400 ml-1">({mmsPercentage}%)</span>
              </span>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={mmsPercentage}
                onChange={(e) => setMmsPercentage(parseInt(e.target.value, 10))}
                className="w-full"
              />
            </label>
          </div>

          {/* Results */}
          {loading && <div className="text-xs text-gray-400">Calculating…</div>}
          {error && <div className="text-xs text-red-600">{error}</div>}
          {estimate && !loading && (
            <div className="bg-white rounded-md border border-gray-200 p-3 text-xs space-y-1.5">
              <div className="flex justify-between text-gray-600">
                <span>Contacts (pending)</span>
                <span className="font-mono">{estimate.contact_count.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>SMS sends</span>
                <span className="font-mono">
                  {estimate.sms_count.toLocaleString()} × {avgSegments} seg = {money(estimate.sms_cost)}
                </span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>MMS sends</span>
                <span className="font-mono">
                  {estimate.mms_count.toLocaleString()} = {money(estimate.mms_cost)}
                </span>
              </div>
              <div className="flex justify-between border-t pt-1.5 font-medium text-gray-800">
                <span>Estimated total</span>
                <span className="font-mono">{money(estimate.total_estimated)}</span>
              </div>
              {estimate.already_spent > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>Already spent</span>
                  <span className="font-mono">{money(estimate.already_spent)}</span>
                </div>
              )}
              {estimate.budget_cap != null && (
                <>
                  <div className="flex justify-between text-gray-600">
                    <span>Projected total</span>
                    <span className="font-mono">{money(estimate.projected_total)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Budget cap</span>
                    <span className="font-mono">{money(estimate.budget_cap)}</span>
                  </div>
                  {estimate.over_budget && (
                    <div className="mt-2 flex items-start gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>
                        Projected cost ({money(estimate.projected_total)}) exceeds the budget cap
                        ({money(estimate.budget_cap)}). Raise the cap or reduce scope before launching.
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
